import { IInterestManagementData } from '@/app/interest-management/entities/interest-management-data.interface';
import { IInterestManagementState } from '@/app/interest-management/entities/interest-management-state.interface';
import { InterestType } from '@/app/subscription-ui/entities/interest.type';
import { IFormGroupState } from '@/components/atoms/form/form-group/form-group-state.interface';
import { NoticeType } from '@/components/molecules/notices/notice-type.definition';
import { InterestEntity } from '../../../../../modules/resource/interest/interest-entity.interface';
import { IInterestState } from './interest-state.interface';

export class InterestManagementFactory {

    static initialState(data: IInterestManagementData): IInterestManagementState {

        return {
            authenticationError: data.authenticationError,
            automaticSubscription: false,
            email: this.buildEmailStateFrom(data),
            interests: this.buildInterests(data),
            removedInterests: [],

            existingUser: data.user !== null,
            eventimInterests: this.getGeneralInterestState(data),
            noContactInterest: this.getNoContactInterestState(data),

            automaticSubscriptionNotice: { type: NoticeType.Info, state: false, title: null, content: null },
            notice: { type: NoticeType.Info, state: false, title: null, content: null },

            topicInterests: this.buildTopicInterests(data),
            topicInterestQuery: { type: null, name: { value: '', valid: false, errors: null} },
            topicInterestRequestInProgress: false,
            topicInterestResults: [],

            artistInterests: this.buildArtistInterests(data),
            artistInterestQuery: { type: { value: 'artist', label: 'Artist alarms' }, name: { value: '', valid: false, errors: null} },
            artistInterestRequestInProgress: false,
            artistInterestResults: [],
        };
    }

    private static buildInterests(data: IInterestManagementData): InterestEntity[] {
        return data.user === null
            ? [{ resourceId: 0, type: InterestType.General, resourceName: 'general' }]
            : data.user.interestData.interests;
    }

    private static buildArtistInterests(data: IInterestManagementData): IInterestState[] {
        if (data.user === null) {
            return [];
        }

        return data.user.interestData.interests
            .filter(i => i.type === InterestType.Artist)
            .map(i => ({ ...i, state: true, initialState: true, title: i.resourceName }));
    }

    private static buildEmailStateFrom(data: IInterestManagementData): IFormGroupState {
        const state = { value: '', valid: false, errors: null };

        if (data.user) {
            state.value = data.user.email;
            state.valid = true;
            return state;
        }

        if (data.userEmail) {
            state.value = data.userEmail;
            state.valid = true;
        }

        return state;
    }

    private static buildTopicInterests(data: IInterestManagementData): IInterestState[] {
        if (data.user === null) {
            return [];
        }

        return data.user.interestData.interests
            .filter(i => i.type === InterestType.Category || i.type === InterestType.MainCategory || i.type === InterestType.Venue)
            .map(i => ({ ...i, state: true, initialState: true, title: i.resourceName }));
    }

    static interestTypeOptions(): { value: string; label: string; }[] {
        return [
            { value: 'category', label: 'Category' },
            { value: 'venue', label: 'Venue' },
        ];
    }

    private static getGeneralInterestState(data: IInterestManagementData): IInterestState[] {
        return [
            {
                resourceId: 0,
                type: InterestType.General,
                resourceName: 'general',
                initialState: data.user === null ? false : this.hasUserInterestOf(InterestType.General, data),
                state: data.user === null ? true : this.hasUserInterestOf(InterestType.General, data),
            },
            {
                resourceId: -1,
                type: InterestType.Consent,
                resourceName: 'offers',
                initialState: data.user === null ? false : data.user?.interestData.marketingConsent,
                state: data.user === null ? true : data.user.interestData.marketingConsent,
            },
        ];
    }

    private static getNoContactInterestState(data: IInterestManagementData): IInterestState {

        return {
            resourceId: -100,
            type: InterestType.NoContact,
            resourceName: 'no-contact',
            initialState: data.user === null ? false : data.user?.interestData.doNotContact,
            state: data.user === null ? false : data.user?.interestData.doNotContact,
        };
    }

    private static hasUserInterestOf(type: InterestType, data: IInterestManagementData): boolean {
        if (data.user === null) {
            return false;
        }

        if (data.user.interestData.interests.length === 0) {
            return false;
        }

        return data.user.interestData.interests.filter(interest => interest.type === type).length !== 0;
    }

}
